// Poste opérateur : écran tablette fixé près de la machine. Gros boutons, fort contraste, tout horodaté.
import { Fragment, useCallback, useEffect, useState } from "react";
import { ApiError, type Api } from "../data/api.ts";
import { ALEA_CAUSES, aleasFor, paramSummary } from "../data/catalog.ts";
import { DemoApi } from "../data/demoApi.ts";
import { DEFAULT_HOURS, OPERATION_LABEL, OPERATOR_ROLE_LABEL, incidentLabel, type DeviceAction, type DeviceState, type JobDocument, type Machine, type OperatorRole, type TaskWithJob, type WorkingHours } from "../data/types.ts";
import { setupKey } from "../lib/optimizer.ts";
import { addDays, addWorkingMinutes, dayRanges, fmtDateTime, fmtDuration, fmtTime, startOfDay } from "../lib/time.ts";
import { Check, Logo, formatColor, toast, useNow } from "../ui/kit.tsx";
import { DocViewer } from "../ui/DocViewer.tsx";

interface Op { id: string; name: string; pin: string; role: OperatorRole }

const tokenKey = (api: Api) => `ordopli-poste-${api.mode}`;
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null) => { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* ignore */ } },
};
const opStore = {
  get: (k: string): Op | null => { try { return JSON.parse(sessionStorage.getItem(k) || "null"); } catch { return null; } },
  set: (k: string, v: Op | null) => { try { v ? sessionStorage.setItem(k, JSON.stringify(v)) : sessionStorage.removeItem(k); } catch { /* ignore */ } },
};

export function PostePage({ api }: { api: Api }) {
  const key = tokenKey(api);
  const [token, setToken] = useState<string | null>(() => store.get(key));
  // une session sans rôle (ancienne version) redemande le code
  const [op, setOp] = useState<Op | null>(() => { const o = opStore.get(key + "-op"); return o?.role ? o : null; });
  const [state, setState] = useState<DeviceState | null>(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => { document.body.classList.add("poste-body"); return () => document.body.classList.remove("poste-body"); }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    try { setState(await api.deviceState(token)); setOffline(false); }
    catch (e) {
      if (e instanceof ApiError && (e.status === 401 || /non reconnue/i.test(e.message))) { store.set(key, null); setToken(null); setState(null); }
      else setOffline(true);
    }
  }, [api, token, key]);

  useEffect(() => {
    refresh();
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 8000);
    const unsub = api.subscribe(refresh);
    return () => { clearInterval(t); unsub(); };
  }, [api, refresh]);

  const act = async (a: DeviceAction): Promise<boolean> => {
    if (!token || !op) return false;
    try { setState(await api.deviceAction(token, op.id, op.pin, a)); setOffline(false); return true; }
    catch (e) {
      const msg = (e as Error).message;
      if (/Code opérateur/i.test(msg)) { opStore.set(key + "-op", null); setOp(null); }
      toast(msg, "err");
      return false;
    }
  };

  if (!token) return <Pairing api={api} onPaired={(t) => { store.set(key, t); setToken(t); }} />;
  if (!state) return <div className="poste poste-center"><Logo size={48} light /><p>{offline ? "Connexion impossible. Nouvel essai dans quelques secondes…" : "Chargement du poste…"}</p></div>;
  if (!op) return <OperatorLogin api={api} token={token} state={state} onLogin={(o) => { opStore.set(key + "-op", o); setOp(o); }} />;
  const docs: DocsAccess = {
    list: (jobId) => api.deviceDocuments(token, jobId),
    page: (docId, page) => api.deviceDocumentPage(token, docId, page),
  };
  return <Station state={state} op={op} offline={offline} act={act} docs={docs} onSwitch={() => { opStore.set(key + "-op", null); setOp(null); }} />;
}

// ---------------------------------------------------------------------------
// Pavé numérique
// ---------------------------------------------------------------------------
function Keypad({ value, onChange, max, onSubmit, submitLabel }: { value: string; onChange: (v: string) => void; max: number; onSubmit?: () => void; submitLabel?: string }) {
  const press = (d: string) => { if (value.length < max) onChange(value + d); };
  return (
    <div className="keypad">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <button key={d} onClick={() => press(d)}>{d}</button>)}
      <button className="kp-del" onClick={() => onChange(value.slice(0, -1))} aria-label="Effacer">⌫</button>
      <button onClick={() => press("0")}>0</button>
      {onSubmit ? <button className="kp-ok" onClick={onSubmit} disabled={!value}>{submitLabel || "OK"}</button> : <span />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Association de la tablette
// ---------------------------------------------------------------------------
function Pairing({ api, onPaired }: { api: Api; onPaired: (token: string) => void }) {
  const [code, setCode] = useState("");
  const [machines, setMachines] = useState<Machine[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (api instanceof DemoApi) api.machines().then(setMachines); }, [api]);
  const submit = async () => {
    setBusy(true);
    try { onPaired(await api.pairDevice(code, "Tablette")); }
    catch (e) { toast((e as Error).message, "err"); setCode(""); }
    finally { setBusy(false); }
  };
  return (
    <div className="poste poste-center">
      <div className="pairing">
        <div className="poste-brand"><Logo size={40} light /> Ordopli · Poste</div>
        {api instanceof DemoApi && machines ? (
          <>
            <h1>Quelle machine ?</h1>
            <p className="muted">Démonstration : choisissez la machine de cette tablette.</p>
            <div className="big-list">
              {machines.map((m) => <button key={m.id} className="big-btn" onClick={() => onPaired((api as DemoApi).pairMachine(m.id))}>{m.name}</button>)}
            </div>
          </>
        ) : (
          <>
            <h1>Associer cette tablette</h1>
            <p className="muted">Le chef d'atelier obtient le code dans Paramètres › Tablettes des postes.</p>
            <div className="code-display code8">{(code + "________").slice(0, 8).split("").map((c, i) => <span key={i} className={c === "_" ? "" : "filled"}>{c === "_" ? "" : c}</span>)}</div>
            <Keypad value={code} onChange={setCode} max={8} onSubmit={code.length === 8 && !busy ? submit : undefined} submitLabel="Valider" />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Choix de l'opérateur
// ---------------------------------------------------------------------------
function OperatorLogin({ api, token, state, onLogin }: { api: Api; token: string; state: DeviceState; onLogin: (o: Op) => void }) {
  const [who, setWho] = useState<DeviceState["operators"][number] | null>(null);
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(0);
  const now = useNow(15_000);
  const submit = async (p = pin) => {
    if (!who) return;
    try { await api.deviceAction(token, who.id, p, { type: "login" }); onLogin({ id: who.id, name: who.name, role: who.role || "conducteur", pin: p }); }
    catch (e) { toast((e as Error).message, "err"); setPin(""); setShake((x) => x + 1); }
  };
  return (
    <div className="poste poste-center">
      <div className="pairing">
        <div className="poste-brand"><Logo size={40} light /> {state.machine.name}</div>
        <p className="muted login-date">{now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
        {!who ? (
          <>
            <h1>Qui est au poste ?</h1>
            {!state.operators.length && <p className="muted">Aucun opérateur autorisé sur ce poste. Le chef d'atelier les ajoute dans Paramètres › Opérateurs.</p>}
            <div className="big-list">
              {state.operators.map((o) => (
                <button key={o.id} className="big-btn who-btn" onClick={() => setWho(o)}>
                  <span className="who-avatar">{o.name.slice(0, 1).toUpperCase()}</span>
                  <span>{o.name}</span>
                  {o.role && o.role !== "conducteur" && <small>{OPERATOR_ROLE_LABEL[o.role]}</small>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <h1>{who.name}, votre code</h1>
            <div key={shake} className={`code-display pin ${shake ? "shake" : ""}`}>{(pin + "____").slice(0, 4).split("").map((c, i) => <span key={i} className={c === "_" ? "" : "filled"}>{c === "_" ? "" : "●"}</span>)}</div>
            <Keypad value={pin} max={4} onChange={(v) => { setPin(v); if (v.length === 4) submit(v); }} />
            <button className="link-light" onClick={() => { setWho(null); setPin(""); }}>← Ce n'est pas moi</button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Écran principal du poste
// ---------------------------------------------------------------------------
type View = "job" | "jour";
interface DocsAccess { list: (jobId: string) => Promise<JobDocument[]>; page: (docId: string, page: number) => Promise<string> }

/** Documents du job affiché, relus quand le job change (et toutes les deux minutes). */
function useJobDocs(docs: DocsAccess, jobId: string | null): JobDocument[] {
  const [list, setList] = useState<JobDocument[]>([]);
  useEffect(() => {
    if (!jobId) { setList([]); return; }
    let alive = true;
    const load = () => docs.list(jobId).then((l) => { if (alive) setList(l); }).catch(() => {});
    setList([]); load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 120_000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  return list;
}

function Station({ state, op, offline, act, docs, onSwitch }: { state: DeviceState; op: Op; offline: boolean; act: (a: DeviceAction) => Promise<boolean>; docs: DocsAccess; onSwitch: () => void }) {
  const now = useNow(1000);
  const [view, setView] = useState<View>("job");
  const [finish, setFinish] = useState(false);
  const [qtyPad, setQtyPad] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const t = state.current;
  const inc = state.incident;
  const chef = op.role !== "conducteur";
  const names = new Map(state.operators.map((o) => [o.id, o.name]));
  const aleas = aleasFor(state.machine.type, state.aleas_type || [], state.aleas_machine || []);
  const upcoming = (state.day || []).filter((x) => x.status === "a_planifier" || x.status === "planifie").length;
  const jobDocs = useJobDocs(docs, t ? t.job_id : null);
  const [viewDoc, setViewDoc] = useState<string | null>(null);

  const finishJob = async (qty: number, waste: number) => {
    if (!t) return;
    if (await act({ type: "termine", task_id: t.id, qty, waste })) {
      setFinish(false);
      setDone(t.job.of_number);
      window.setTimeout(() => setDone(null), 1600);
    }
  };

  return (
    <div className={`poste station ${inc ? "has-incident" : ""}`}>
      <header className="poste-head">
        <div className="poste-brand"><Logo size={30} light /> <b>{state.machine.name}</b></div>
        <div className="poste-tabs" role="tablist">
          <button role="tab" aria-selected={view === "job"} className={view === "job" ? "on" : ""} onClick={() => setView("job")}>Job en cours</button>
          <button role="tab" aria-selected={view === "jour"} className={view === "jour" ? "on" : ""} onClick={() => setView("jour")}>Planning du jour{upcoming ? <span className="tab-count">{upcoming}</span> : null}</button>
        </div>
        <div className="poste-who">
          {offline && <span className="offline">Hors ligne</span>}
          <span className="who-chip"><span className="who-avatar sm">{op.name.slice(0, 1).toUpperCase()}</span>{op.name}{chef && <small>{OPERATOR_ROLE_LABEL[op.role]}</small>}</span>
          <button className="btn-dark-sm" onClick={onSwitch}>Changer</button>
        </div>
        <div className="poste-clock">
          <span>{now.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>
          <b>{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</b>
        </div>
      </header>

      {inc ? <IncidentPanel inc={inc} t={t} now={now} act={act} /> : view === "jour" ? (
        <DayPlan state={state} op={op} names={names} act={async (a) => { const ok = await act(a); if (ok) setView("job"); return ok; }} />
      ) : (
        <div className="poste-grid">
          <section className="poste-job" key={t?.id || "vide"}>
            {!t ? (
              <div className="poste-empty"><h2>Aucun job publié pour ce poste</h2><p>Le chef d'atelier publie le planning depuis Ordopli. Cet écran se met à jour tout seul.</p></div>
            ) : (
              <JobCard t={t} state={state} op={op} names={names} now={now} act={act} docs={jobDocs} onDoc={setViewDoc} onFinish={() => setFinish(true)} onQty={() => setQtyPad(true)} />
            )}
          </section>
          <aside className="poste-side">
            <h2>Signaler un aléa</h2>
            <div className={`incident-btns ${aleas.length > 6 ? "dense" : ""}`}>
              {aleas.map((k) => (
                <button key={k} className="inc-btn" onClick={() => act({ type: "alea", task_id: t ? t.id : null, kind: k, comment: "" })}>{k}</button>
              ))}
            </div>
            <h2 className="next-h">Ensuite</h2>
            <div className="next-wrap"><NextJobs state={state} names={names} /></div>
          </aside>
        </div>
      )}

      {finish && t && <FinishModal t={t} onClose={() => setFinish(false)} onDone={finishJob} />}
      {qtyPad && t && <QtyModal initial={t.produced_qty} total={t.job.quantity} onClose={() => setQtyPad(false)} onSave={async (qty) => { if (await act({ type: "quantite", task_id: t.id, qty })) setQtyPad(false); }} />}
      {viewDoc && jobDocs.length > 0 && <DocViewer tablet docs={jobDocs} startDoc={viewDoc} loadPage={docs.page} onClose={() => setViewDoc(null)} />}
      {done && <div className="done-overlay" role="status"><div className="done-check"><Check size={78} /></div><b>{done} terminé</b><span>Bravo, au suivant !</span></div>}
    </div>
  );
}

function JobCard({ t, state, op, names, now, act, docs, onDoc, onFinish, onQty }: {
  t: TaskWithJob; state: DeviceState; op: Op; names: Map<string, string>; now: Date; act: (a: DeviceAction) => Promise<boolean>;
  docs: JobDocument[]; onDoc: (id: string) => void; onFinish: () => void; onQty: () => void;
}) {
  const otherOwner = t.operator_id && t.operator_id !== op.id ? names.get(t.operator_id) || "un collègue" : null;
  const blocked = !!otherOwner && op.role === "conducteur";
  const params = paramSummary(t);
  const started = t.status !== "a_planifier" && t.status !== "planifie";
  const pct = Math.min(100, (t.produced_qty / Math.max(1, t.job.quantity)) * 100);
  const left = Math.max(0, t.job.quantity - t.produced_qty);
  const elapsed = t.started_at ? Math.max(0, Math.floor((now.getTime() - new Date(t.started_at).getTime()) / 60_000)) : 0;
  const setupPlan = t.setup_min ?? state.machine.setup_min;
  const fr = (n: number) => n.toLocaleString("fr-FR").replace(/\u202f/g, "\u00a0");
  return (
    <>
      <div className="pj-body">
        <div className="pj-top">
          <span className="pj-of">{t.job.of_number}</span>
          <StatusPill status={t.status} />
          {t.job.priority === "urgent" && <span className="pill pill-red">Urgent</span>}
          {t.operator_id && <span className={`pill ${t.operator_id === op.id ? "pill-teal" : "pill-grey"}`}>{t.operator_id === op.id ? "Pour vous" : `Attribué à ${otherOwner}`}</span>}
        </div>
        <h1 className="pj-client">{t.job.client}</h1>
        <p className="pj-title">{t.job.title}</p>
        {docs.length > 0 && (
          <div className="pj-docs">
            {docs.slice(0, 3).map((d) => (
              <button key={d.id} className="pj-doc" onClick={() => onDoc(d.id)}>
                <img src={d.thumb} alt="" />
                <span><b>Voir {d.kind === "photo" ? "la photo" : d.kind === "maquette" ? "la maquette" : d.kind === "bat" ? "le BAT" : "le document"}</b><small>{d.pages > 1 ? `${d.pages} pages` : "1 page"} · toucher pour agrandir</small></span>
              </button>
            ))}
          </div>
        )}
        <div className="pj-facts">
          <div className="pj-fact-main"><span>Opération</span><b>{OPERATION_LABEL[t.operation]}{t.fold_type ? ` ${t.fold_type}` : ""}</b></div>
          <div className="pj-fact-main"><span>Format</span><b><i className="pj-swatch" style={{ background: formatColor(t.format_out) }} />{t.format_in} → {t.format_out}</b></div>
          <div><span>Quantité</span><b>{fr(t.job.quantity)}</b></div>
          <div><span>Papier</span><b>{t.paper || "—"}{t.grammage ? ` ${t.grammage}\u00a0g` : ""}{t.pages ? ` · ${t.pages} p.` : ""}</b></div>
          <div><span>Échéance</span><b>{fmtDateTime(t.job.due_at)}</b></div>
          {params.map((p) => <div key={p.label}><span>{p.label}</span><b>{p.value === p.label ? "Oui" : p.value}</b></div>)}
        </div>
        {t.packaging && <div className="pj-pack"><span>Conditionnement</span><b>{t.packaging}</b></div>}
        {t.job.notes && <div className="pj-notes"><b>Consignes</b><p>{t.job.notes}</p></div>}
      </div>
      <div className="pj-foot">
        {started && (
          <div className="pj-progress">
            <div className="pj-qty">
              <b>{fr(t.produced_qty)}</b><span>/ {fr(t.job.quantity)} ex.</span>
              <em>{t.produced_qty ? `${Math.round(pct)}\u00a0% · reste ${fr(left)}` : t.status === "calage" ? `Calage depuis ${fmtDuration(elapsed)}${setupPlan ? ` · prévu ~${fmtDuration(setupPlan)}` : ""}` : "Aucune quantité saisie"}</em>
              <button className="btn-qty" onClick={onQty} disabled={blocked}>Saisir la quantité</button>
            </div>
            <div className="progress big"><i style={{ width: `${pct}%` }} /></div>
          </div>
        )}
        {blocked ? (
          <div className="pj-blocked">Ce job est attribué à {otherOwner}. Un chef d'atelier peut le démarrer à sa place.</div>
        ) : (
          <div className="pj-actions">
            {!started && <button className="big-action a-calage" onClick={() => act({ type: "calage", task_id: t.id })}>Démarrer le calage</button>}
            {t.status === "calage" && <button className="big-action a-prod" onClick={() => act({ type: "production", task_id: t.id })}>Lancer la production</button>}
            {t.status === "production" && <>
              <button className="big-action a-pause sm" onClick={() => act({ type: "pause", task_id: t.id })}>Pause</button>
              <button className="big-action a-done" onClick={onFinish}>Terminé</button>
            </>}
            {t.status === "pause" && <>
              <button className="big-action a-prod" onClick={() => act({ type: "reprise", task_id: t.id })}>Reprendre</button>
              <button className="big-action a-done sm" onClick={onFinish}>Terminé</button>
            </>}
          </div>
        )}
        {t.started_at && t.status !== "calage" && <p className="pj-since">Démarré à {fmtTime(t.started_at)}</p>}
      </div>
    </>
  );
}

function StatusPill({ status }: { status: TaskWithJob["status"] }) {
  const map: Record<string, [string, string]> = {
    a_planifier: ["À démarrer", ""], planifie: ["À démarrer", ""], calage: ["En calage", "pill-blue"],
    production: ["En production", "pill-teal live"], pause: ["En pause", "pill-grey"], termine: ["Terminé", ""],
  };
  const [l, c] = map[status];
  return <span key={status} className={`pill pill-pop ${c}`}>{l}</span>;
}

function NextJobs({ state, names }: { state: DeviceState; names: Map<string, string> }) {
  if (!state.next.length) return <p className="muted-light">Rien d'autre de publié pour l'instant.</p>;
  let prevKey = state.current ? setupKey(state.current) : null;
  return (
    <ol className="next-list">
      {state.next.map((t) => {
        const same = prevKey === setupKey(t);
        prevKey = setupKey(t);
        return (
          <li key={t.id}>
            <div><b>{t.job.of_number}</b> {t.job.client}{t.pub_start && <span className="next-time">{fmtTime(t.pub_start)}</span>}</div>
            <div className="small">{OPERATION_LABEL[t.operation]} {t.format_in} → {t.format_out} · {t.job.quantity.toLocaleString("fr-FR")} ex.{t.operator_id ? ` · ${names.get(t.operator_id) || ""}` : ""}</div>
            <span className={`pill ${same ? "pill-teal" : "pill-orange"}`}>{same ? "Même réglage" : `Recalage ~${fmtDuration(state.machine.setup_min)}`}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** La journée du poste, dans l'ordre : ce qui est fait, ce qui tourne, ce qui vient. */
function DayPlan({ state, op, names, act }: { state: DeviceState; op: Op; names: Map<string, string>; act: (a: DeviceAction) => Promise<boolean> }) {
  const day = state.day || [];
  const chef = op.role !== "conducteur";
  const running = day.some((t) => ["calage", "production", "pause"].includes(t.status));
  const doneCount = day.filter((t) => t.status === "termine").length;
  const totalMin = day.filter((t) => t.status !== "termine").reduce((a, t) => a + (t.setup_min || 0) + (t.run_min || 0), 0);
  let prevKey: string | null = null;
  let lastDay: string | null = null;
  const [focus, setFocus] = useState<string | null>(null);
  const now = useNow(30_000);
  const hours = state.hours || DEFAULT_HOURS;
  const bars = barsOf(day, hours, state.machine.setup_min, now);
  const today = startOfDay(now);
  const days: Date[] = [today];
  for (const b of bars) { const d = startOfDay(b.start); if (!days.some((x) => x.getTime() === d.getTime())) days.push(d); }
  days.sort((a, b) => a.getTime() - b.getTime());
  const todayHasTodo = bars.some((b) => b.t.status !== "termine" && startOfDay(b.start).getTime() === today.getTime());
  const firstIdx = Math.max(0, days.findIndex((d) => d.getTime() === today.getTime()));
  const [dayIdx, setDayIdx] = useState(todayHasTodo || firstIdx === days.length - 1 ? firstIdx : firstIdx + 1);
  const selDay = days[Math.min(dayIdx, days.length - 1)];
  const selEnd = addDays(selDay, 1);
  const visible = bars.filter((b) => b.end > selDay && b.start < selEnd).map((b) => b.t);
  const pick = (id: string) => {
    setFocus(id);
    document.getElementById(`dp-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  return (
    <div className="day-plan">
      <div className="dp-head">
        <h2>Planning du jour</h2>
        <span>{doneCount} terminé{doneCount > 1 ? "s" : ""} aujourd'hui · {day.length - doneCount} à faire{totalMin ? ` · environ ${fmtDuration(totalMin)} de travail` : ""}</span>
      </div>
      {day.length > 0 && <PosteTimeline bars={bars} days={days} dayIdx={Math.min(dayIdx, days.length - 1)} onDay={setDayIdx} hours={hours} now={now} focus={focus} onPick={pick} />}
      {!day.length ? <div className="poste-empty"><h2>Rien de prévu aujourd'hui sur ce poste</h2><p>Le planning publié apparaît ici dès que le chef d'atelier l'envoie.</p></div> : (
        <ol className="dp-list">
          {!visible.length && <li className="dp-empty">Rien de prévu ce jour-là sur ce poste.</li>}
          {visible.map((t) => {
            const when = t.status === "termine" ? t.finished_at : t.started_at || t.pub_start;
            const dayLabel = when ? dayName(new Date(when)) : "Sans horaire";
            const sep = dayLabel !== lastDay && (lastDay !== null || dayLabel !== "Aujourd'hui");
            lastDay = dayLabel;
            const key = setupKey(t);
            const recal = prevKey !== null && prevKey !== key;
            prevKey = key;
            const mine = t.operator_id === op.id;
            const st = t.status === "termine" ? "done" : ["calage", "production", "pause"].includes(t.status) ? "live" : "todo";
            const time = t.status === "termine" ? t.finished_at : t.started_at || t.pub_start;
            return (
              <Fragment key={t.id}>
              {sep && <li className="dp-sep">{dayLabel}</li>}
              <li id={`dp-${t.id}`} className={`dp-item dp-${st} ${mine ? "mine" : ""} ${focus === t.id ? "focus" : ""}`}>
                <div className="dp-time">{time ? fmtTime(time) : "—"}<span>{st === "done" ? "fini" : st === "live" ? "en cours" : "prévu"}</span></div>
                <div className="dp-rail"><i /></div>
                <div className="dp-card">
                  <div className="dp-top"><b>{t.job.of_number}</b> {t.job.client}{t.job.priority === "urgent" && <span className="pill pill-red">Urgent</span>}</div>
                  <div className="dp-meta">{OPERATION_LABEL[t.operation]} {t.format_in} → {t.format_out}{t.fold_type ? ` · ${t.fold_type}` : ""} · {t.job.quantity.toLocaleString("fr-FR")} ex.{t.packaging ? ` · ${t.packaging}` : ""}</div>
                  <div className="dp-tags">
                    {st === "done" && <span className="pill">Fait · {t.produced_qty.toLocaleString("fr-FR")} bons</span>}
                    {st === "todo" && recal && <span className="pill pill-orange">Recalage</span>}
                    {t.operator_id && <span className={`pill ${mine ? "pill-teal" : "pill-grey"}`}>{mine ? "Pour vous" : names.get(t.operator_id) || "Attribué"}</span>}
                    {st === "todo" && (t.setup_min || t.run_min) ? <span className="dp-dur">{fmtDuration((t.setup_min || 0) + (t.run_min || 0))}</span> : null}
                  </div>
                </div>
                {chef && st === "todo" && !running && <button className="btn-dark-sm dp-start" onClick={() => act({ type: "calage", task_id: t.id })}>Démarrer maintenant</button>}
              </li>
              </Fragment>
            );
          })}
        </ol>
      )}
      {!chef && <p className="muted-light dp-note">Seul un chef d'atelier peut changer l'ordre des jobs depuis la tablette.</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Frise du poste, comme le planning du chef : une journée, la machine du poste
// ---------------------------------------------------------------------------
interface Bar { t: TaskWithJob; start: Date; end: Date; setupEnd: Date }

function barsOf(tasks: TaskWithJob[], hours: WorkingHours, setupMin: number, now: Date): Bar[] {
  return tasks.map((t) => {
    const dur = (t.setup_min ?? setupMin) + (t.run_min ?? 30);
    let start: Date, end: Date;
    if (t.status === "termine") {
      start = new Date(t.started_at || t.finished_at!); end = new Date(t.finished_at || t.started_at!);
    } else if (t.started_at) {
      start = new Date(t.started_at);
      const planned = t.planned_end ? new Date(t.planned_end) : addWorkingMinutes(start, dur, hours);
      end = planned > now ? planned : now;
    } else {
      start = new Date(t.pub_start || t.planned_start || now);
      end = addWorkingMinutes(start, dur, hours);
    }
    const setupEnd = t.status === "termine" || t.started_at ? start : addWorkingMinutes(start, t.setup_min ?? 0, hours);
    return { t, start, end, setupEnd };
  });
}

function PosteTimeline({ bars, days, dayIdx, onDay, hours, now, focus, onPick }: {
  bars: Bar[]; days: Date[]; dayIdx: number; onDay: (i: number) => void; hours: WorkingHours; now: Date; focus: string | null; onPick: (id: string) => void;
}) {
  const day = days[dayIdx];
  const today = startOfDay(now);
  const ranges = dayRanges(day, hours);
  const toMin = (d: Date) => d.getHours() * 60 + d.getMinutes();
  const w0 = ranges.length ? toMin(ranges[0][0]) : 8 * 60;
  const w1 = ranges.length ? toMin(ranges[ranges.length - 1][1]) : 17 * 60;
  const span = Math.max(60, w1 - w0);
  const pct = (d: Date) => {
    const same = startOfDay(d).getTime() === day.getTime();
    const m = same ? toMin(d) : d < day ? w0 : w1;
    return (Math.min(Math.max(m - w0, 0), span) / span) * 100;
  };
  const end = addDays(day, 1);
  const shown = bars.filter((b) => b.end > day && b.start < end);
  const ticks: number[] = [];
  for (let h = Math.ceil(w0 / 60); h * 60 <= w1; h++) ticks.push(h);
  const breaks = ranges.slice(1).map((r, i) => [toMin(ranges[i][1]), toMin(r[0])]);
  const isToday = day.getTime() === today.getTime();
  return (
    <div className="ptl">
      <div className="ptl-days">
        {days.slice(0, 4).map((d, i) => (
          <button key={d.getTime()} className={i === dayIdx ? "on" : ""} onClick={() => onDay(i)}>{dayName(d)}</button>
        ))}
      </div>
      <div className="ptl-frame">
        <div className="ptl-hours">
          {ticks.map((h) => <span key={h} style={{ left: `${((h * 60 - w0) / span) * 100}%` }}>{h}h</span>)}
        </div>
        <div className="ptl-lane">
          {breaks.map(([a, b2], i) => <div key={i} className="ptl-break" style={{ left: `${((a - w0) / span) * 100}%`, width: `${((b2 - a) / span) * 100}%` }} />)}
          {shown.map((b, i) => {
            const left = pct(b.start), right = pct(b.end), width = Math.max(1.2, right - left);
            const hatch = b.setupEnd > b.start ? Math.min(width, pct(b.setupEnd) - left) : 0;
            const st = b.t.status === "termine" ? "done" : b.t.started_at ? "live" : "todo";
            return (
              <button key={b.t.id} className={`ptl-block ptl-${st} ${focus === b.t.id ? "focus" : ""}`} onClick={() => onPick(b.t.id)}
                style={{ left: `${left}%`, width: `${width}%`, background: formatColor(b.t.format_out), ["--i" as string]: i }}
                title={`${b.t.job.of_number} · ${b.t.job.client} — ${fmtTime(b.start)} → ${fmtTime(b.end)}`}>
                {hatch > 0 && <span className="ptl-hatch" style={{ width: `${(hatch / width) * 100}%` }} />}
                <span className="ptl-text"><b>{b.t.job.of_number}</b> {b.t.job.client}<em>{b.t.format_in}→{b.t.format_out} · {fmtTime(b.start)}</em></span>
              </button>
            );
          })}
          {isToday && pct(now) > 0 && pct(now) < 100 && <div className="ptl-now" style={{ left: `${pct(now)}%` }}><span>{fmtTime(now)}</span></div>}
        </div>
      </div>
      <div className="ptl-legend"><span><i className="lg-hatch" /> calage</span><span><i className="lg-run" /> production (couleur = format de sortie)</span><span>Touchez un job pour voir son détail.</span></div>
    </div>
  );
}

function dayName(d: Date): string {
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  const d0 = new Date(d); d0.setHours(0, 0, 0, 0);
  const diff = Math.round((d0.getTime() - t0.getTime()) / 86_400_000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Demain";
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function IncidentPanel({ inc, t, now, act }: { inc: NonNullable<DeviceState["incident"]>; t: TaskWithJob | null; now: Date; act: (a: DeviceAction) => Promise<boolean> }) {
  const secs = Math.max(0, Math.floor((now.getTime() - new Date(inc.started_at).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, "0"), ss = String(secs % 60).padStart(2, "0");
  const label = incidentLabel(inc.kind);
  const causes = ALEA_CAUSES[label] || [];
  return (
    <div className="incident-panel">
      <div className="ip-head">
        <span className="ip-badge">Machine arrêtée</span>
        <div className="ip-kind">{label}</div>
        {t && <div className="ip-job">{t.job.of_number} · {t.job.client}</div>}
      </div>
      <div className="ip-timer" aria-live="off">{h > 0 && <>{h}<span className="ip-colon">:</span></>}{mm}<span className="ip-colon">:</span>{ss}</div>
      <p>Depuis {fmtTime(inc.started_at)}. Touchez « Reprendre » dès que la machine repart.</p>
      {causes.length > 0 && (
        <div className="ip-causes">
          <span>Cause (facultatif) :</span>
          {causes.map((c) => (
            <button key={c} className={inc.comment === c ? "on" : ""} onClick={() => act({ type: "note_alea", comment: inc.comment === c ? "" : c })}>{c}</button>
          ))}
        </div>
      )}
      <button className="big-action a-prod" onClick={() => act({ type: "fin_alea" })}>Reprendre</button>
    </div>
  );
}

function FinishModal({ t, onClose, onDone }: { t: TaskWithJob; onClose: () => void; onDone: (qty: number, waste: number) => void }) {
  const [field, setField] = useState<"qty" | "waste">("qty");
  const [qty, setQty] = useState(String(t.produced_qty || t.job.quantity));
  const [waste, setWaste] = useState("0");
  return (
    <div className="poste-modal-bg">
      <div className="poste-modal">
        <h2>Terminer le job {t.job.of_number}</h2>
        {t.packaging && <p className="fm-pack">Conditionnement : <b>{t.packaging}</b></p>}
        <div className="fm-fields">
          <button className={field === "qty" ? "on" : ""} onClick={() => setField("qty")}><span>Quantité bonne</span><b>{Number(qty || 0).toLocaleString("fr-FR")}</b></button>
          <button className={field === "waste" ? "on" : ""} onClick={() => setField("waste")}><span>Gâche</span><b>{Number(waste || 0).toLocaleString("fr-FR")}</b></button>
        </div>
        <Keypad value={field === "qty" ? qty : waste} max={7} onChange={(v) => (field === "qty" ? setQty(v) : setWaste(v))} />
        {field === "qty" && Number(qty || 0) !== t.job.quantity && <button className="link-light fm-all" onClick={() => setQty(String(t.job.quantity))}>Mettre la quantité commandée ({t.job.quantity.toLocaleString("fr-FR")})</button>}
        <div className="pm-foot">
          <button className="big-action a-grey" onClick={onClose}>Annuler</button>
          <button className="big-action a-done" onClick={() => onDone(Number(qty || 0), Number(waste || 0))}>Confirmer</button>
        </div>
      </div>
    </div>
  );
}

function QtyModal({ initial, total, onClose, onSave }: { initial: number; total: number; onClose: () => void; onSave: (q: number) => void }) {
  const [v, setV] = useState(initial ? String(initial) : "");
  const n = Number(v || 0);
  const add = (k: number) => setV(String(Math.max(0, Math.min(9_999_999, n + k))));
  return (
    <div className="poste-modal-bg">
      <div className="poste-modal">
        <h2>Quantité produite</h2>
        <div className="qty-display"><b>{n.toLocaleString("fr-FR")}</b><span>/ {total.toLocaleString("fr-FR")} ex.</span></div>
        <div className="qty-quick">
          {[100, 500, 1000].map((k) => <button key={k} onClick={() => add(k)}>+{k.toLocaleString("fr-FR")}</button>)}
          <button onClick={() => setV(String(total))}>Tout</button>
        </div>
        <Keypad value={v} onChange={setV} max={7} />
        <div className="pm-foot">
          <button className="big-action a-grey" onClick={onClose}>Annuler</button>
          <button className="big-action a-prod" onClick={() => onSave(n)}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
